import React from 'react';
import { Alert, Button, Table, Tag, Typography } from 'antd';
import { useNavigation } from '@refinedev/core';
import type { ColumnsType } from 'antd/es/table';
import type { PagedListState } from '@/hooks/usePagedList';
import type { SuspiciousPlayer } from '@/types';
import { ListBody, LoadMoreFooter } from './ListParts';
import { riskRowKey, type RiskListInfo } from './riskLists';

const { Text } = Typography;

export const RiskPlayersTab: React.FC<{
  info: RiskListInfo;
  state: PagedListState<SuspiciousPlayer, undefined>;
}> = ({ info, state }) => {
  const { show } = useNavigation();
  const list = state.list;
  const columns: ColumnsType<SuspiciousPlayer> = [
    {
      title: 'Player',
      dataIndex: 'displayName',
      width: 150,
      render: (name: string, record) => (
        <Button type="link" size="small" onClick={() => show('players', record.userId)}>
          {name}
        </Button>
      ),
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      width: 90,
      render: (p: string) => <Tag color={p === 'android' ? 'green' : 'blue'}>{p}</Tag>,
    },
    { title: 'Metric', dataIndex: 'metric', width: 160 },
    {
      title: 'Value',
      dataIndex: 'value',
      width: 110,
      render: (v: number) => (
        <Text strong style={{ color: '#ff4d4f' }}>
          {typeof v === 'number' ? v.toLocaleString() : v}
        </Text>
      ),
    },
    {
      title: 'Threshold',
      dataIndex: 'threshold',
      width: 100,
      render: (v: number) => (v > 0 ? v.toLocaleString() : '-'),
    },
    { title: 'Detail', dataIndex: 'detail', ellipsis: true },
  ];

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {info.description}
      </Text>
      <ListBody state={state}>
        {list && list.total !== undefined && list.total > list.available && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${list.total.toLocaleString()} players are flagged. The first ${list.available.toLocaleString()} (${info.order}) can be loaded here and in the export.`}
          />
        )}
        <Table<SuspiciousPlayer>
          dataSource={list?.rows ?? []}
          columns={columns}
          rowKey={riskRowKey}
          size="small"
          pagination={false}
          scroll={{ x: 800 }}
        />
        <LoadMoreFooter
          state={state}
          noun={info.name === 'largeTransactions' ? 'transactions' : 'players'}
        />
      </ListBody>
    </div>
  );
};
